import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { cn } from "@/lib/utils";
import { enableAutoSuggest, bucketUrlPrefix } from "common/config";
import { getSuggestions, Suggestion, RecentSearchResult } from "common/apis";
import { useFilters, useI18N, useBrands } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import {
  SearchSuggestionsDropdown,
  type NeighborhoodSuggestion,
} from "@/components/Search/FilterPannel/SearchSuggestions";
import { MagnifyingGlassIcon, MapPinIcon } from "@phosphor-icons/react";
import { CookieIcon, CoffeeIcon, MartiniIcon, ForkKnifeIcon, ShoppingCartIcon} from "@phosphor-icons/react";


// Re-export types for external use
export type { RecentSearchResult, NeighborhoodSuggestion };

type AutoSuggestInputProps = {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (text: string) => void;
  onSelectSuggestion: (suggestion: Suggestion) => void;
  onClear?: () => void;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  className?: string;
  tagging_mode?: string;
  types?: string;
  accessibilities?: string;
  showVenueOptions?: string;
  // SearchSuggestionsDropdown callbacks
  onNearbyPlacesClick?: () => void;
  onRecentSearchSelect?: (search: RecentSearchResult) => void;
  onNeighborhoodSelect?: (neighborhood: NeighborhoodSuggestion) => void;
  onLoginPress?: () => void;
  /** Whether to show the SearchSuggestionsDropdown when input is empty/focused */
  showSearchSuggestions?: boolean;
  /** Suppress the autosuggest dropdown (e.g., when saved searches is open) */
  suppressDropdown?: boolean;
  /** Layout mode - determines positioning and wrapper behavior */
  layout?: "default" | "header" | "overlay" | "overlaymobile";
};

// Helper function to sanitize input by removing HTML tags and normalizing whitespace
const sanitizeInput = (text: string): string => {
  const withoutHtml = text.replace(/<[^>]*>/g, "");
  return withoutHtml.trim().replace(/\s+/g, " ");
};

// Helper function to format text by removing underscores and capitalizing each word
const formatTextForDisplay = (text: string): string => {
  if (!text) return "";

  // Replace underscores with spaces
  const withoutUnderscores = text.replace(/_/g, " ");

  // Capitalize each word
  return withoutUnderscores
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function to format display text based on suggestion type
const formatDisplayText = (
  suggestion: Suggestion,
  currentValue: string,
): string => {
  if (suggestion.suggestion_type === "brand") {
    // For brand suggestions, use the brand name with formatting
    return formatTextForDisplay(suggestion.brand || currentValue);
  } else if (suggestion.suggestion_type === "location") {
    // For location suggestions, use the text
    return sanitizeInput(suggestion.text || "");
  } else if (suggestion.suggestion_type === "business_type") {
    // For business_type suggestions, use the business type with formatting
    return formatTextForDisplay(suggestion.business_type || currentValue);
  } else {
    // For other suggestions, use name and city
    const nameSan = sanitizeInput(
      suggestion.name || suggestion.text || currentValue,
    );
    const citySan = sanitizeInput(suggestion.city || "");
    return citySan ? `${nameSan}, ${citySan}` : nameSan;
  }
};

// Helper function to get the appropriate icon based on suggestion type
const getSuggestionIcon = (
  suggestionType?: string,
  businessTypeValue?: string | null,
  brandName?: string | null,
  latitude?: number,
  longitude?: number,
) => {
  // For brand suggestions, use the brand logo component
  if (suggestionType === "brand" && brandName) {
    return (
      <BrandLogo
        brandName={brandName}
        latitude={latitude || 0}
        longitude={longitude || 0}
      />
    );
  }

  // For business_type and venue_name suggestions, use the business type icon from the filters
  if (
    (suggestionType === "business_type" || suggestionType === "venue_name") &&
    businessTypeValue
  ) {
    return <BusinessTypeIcon businessType={businessTypeValue} />;
  }

  // For other suggestion types, use the default icons
  switch (suggestionType) {
    case "location":
      return (
        <MapPinIcon size={20} className="text-fg-brand-subtle flex-shrink-0" />
      );
    default:
      return (
        <MapPinIcon size={20} className="text-fg-brand-subtle flex-shrink-0" />
      );
  }
};

// Component to display business type icons
const BusinessTypeIcon = ({ businessType }: { businessType: string }) => {
  const { businessTypesFrontend } = useFilters();
  const [iconPath, setIconPath] = useState<string | null>(null);

  // First check for specific hardcoded business type icons
  const hasSpecificIcon = ["bakery", "cafe", "bar", "restaurant", "supermarket", "food","store"].includes(businessType);

  // Find the matching business type and get its icon from businessTypesFrontend
  useEffect(() => {
    // Skip fetching if we have a specific icon
    if (hasSpecificIcon) return;
    
    if (businessTypesFrontend && businessType) {
      const matchingFrontendType = businessTypesFrontend.find(
        (type) => type.name.toLowerCase() === businessType.toLowerCase(),
      );
      if (matchingFrontendType && matchingFrontendType.image) {
        setIconPath(bucketUrlPrefix + matchingFrontendType.image);
        return;
      }
    }
  }, [businessTypesFrontend, businessType, hasSpecificIcon]);

  // Render specific icon if available
  if (hasSpecificIcon) {
    switch (businessType) {
      case "bakery":
        return <CookieIcon size={20} className="text-fg-brand-subtle" />;
      case "cafe":
        return <CoffeeIcon size={20} className="text-fg-brand-subtle" />;
      case "bar":
        return <MartiniIcon size={20} className="text-fg-brand-subtle" />;
      case "restaurant":
        return <ForkKnifeIcon size={20} className="text-fg-brand-subtle" />;
      case "food":
          return <ShoppingCartIcon size={20} className="text-fg-brand-subtle" />;
      case "supermarket":
        return <ShoppingCartIcon size={20} className="text-fg-brand-subtle" />;
      case "store":
        return <ShoppingCartIcon size={20} className="text-fg-brand-subtle" />;
    }
  }

  // Render dynamic icon from businessTypesFrontend if found
  if (iconPath) {
    return (
      <img
        src={iconPath}
        alt={businessType}
        className="w-6 h-6 object-contain filter brightness-0"
      />
    );
  }

  // Fallback icon if no matching business type is found
  return <MapPinIcon size={20} className="text-fg-brand-subtle flex-shrink-0" />;
};

// Component to display brand logo or first character
interface BrandLogoProps {
  brandName: string;
  latitude: number;
  longitude: number;
}

const BrandLogo = ({ 
  brandName, 
  latitude, 
  longitude 
}: BrandLogoProps) => {
  const { data: brandsData } = useBrands({
    latitude,
    longitude,
    enabled: !!brandName && !!latitude && !!longitude && !isNaN(latitude) && !isNaN(longitude)
  });
  
  const matchingBrand = brandsData?.facets?.brand?.find(
    b => b.value.toLowerCase() === brandName.toLowerCase()
  );
  
  const imageUrl = matchingBrand?.logo 
    ? `${bucketUrlPrefix}${matchingBrand.logo}` 
    : null;
    
  return (
    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-container-neutral-minimal overflow-hidden flex-shrink-0">
      {imageUrl ? (
        <img src={imageUrl} alt={brandName} className="w-full h-full object-cover" />
      ) : (
        <span className="label-s-semibold text-fg-neutral-strong">
          {brandName.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  );
};

export default function AutoSuggestInput({
  label,
  value,
  onValueChange,
  onSubmit,
  onSelectSuggestion,
  onClear,
  latitude = 0,
  longitude = 0,
  radiusKm = 10,
  className,
  tagging_mode,
  types,
  accessibilities,
  showVenueOptions,
  // SearchSuggestionsDropdown callbacks
  onNearbyPlacesClick,
  onRecentSearchSelect,
  onNeighborhoodSelect,
  onLoginPress,
  showSearchSuggestions = false,
  suppressDropdown = false,
  layout = "default" as "default" | "header" | "overlay" | "overlaymobile",
}: AutoSuggestInputProps) {
  const i18n = useI18N();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [isSearchSuggestionsOpen, setIsSearchSuggestionsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const suggestDebounceRef = useRef<number | null>(null);
  const suggestListRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when component mounts
  useEffect(() => {
    if (layout === "overlay" || layout === "overlaymobile") {
      // Small delay to ensure DOM is ready and any animations have started
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [layout]);

  // Close dropdowns when suppressDropdown becomes true
  useEffect(() => {
    if (suppressDropdown) {
      setIsSuggestOpen(false);
      setIsSearchSuggestionsOpen(false);
    }
  }, [suppressDropdown]);

  useEffect(() => {
    if (!isSuggestOpen) return;
    if (highlightIndex < 0) return;
    const activeEl = document.getElementById(`suggest-item-${highlightIndex}`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex, isSuggestOpen]);

  const displayItems = useMemo(() => {
    const items: Array<
      { kind: "query"; text: string } | { kind: "suggestion"; data: Suggestion }
    > = [];
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      items.push({ kind: "query", text: trimmed });
    }
    for (const s of suggestions) {
      items.push({ kind: "suggestion", data: s });
    }
    return items;
  }, [value, suggestions]);

  const clearSuggestions = useCallback(() => {
    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
      suggestDebounceRef.current = null;
    }
    setSuggestions([]);
    setIsSuggestOpen(false);
    setIsSearchSuggestionsOpen(false);
    setHighlightIndex(-1);
  }, []);

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (isSuggestOpen || isSearchSuggestionsOpen) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        clearSuggestions();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSuggestOpen, isSearchSuggestionsOpen, clearSuggestions]);

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightName = (name: string, query: string) => {
    if (!name) return <>{name}</>;
    const tokens = query
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2);
    if (tokens.length === 0) return <>{name}</>;
    const pattern = tokens.map(escapeRegExp).join("|");
    const re = new RegExp(`(${pattern})`, "ig");
    const parts = name.split(re);
    return (
      <>
        {parts.map((part, idx) =>
          re.test(part) ? (
            <b key={idx}>{part}</b>
          ) : (
            <span key={idx}>{part}</span>
          ),
        )}
      </>
    );
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    onValueChange(text);

    if (!enableAutoSuggest || suppressDropdown) return;

    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
    }

    if (text && text.length >= 1) {
      // Close SearchSuggestionsDropdown and open autosuggest when user starts typing
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);

      // Fetch suggestions only when length >= 2
      if (text.length >= 2) {
        suggestDebounceRef.current = window.setTimeout(async () => {
          const list = await getSuggestions(text, {
            fuzzy: true,
            highlights: true,
            latitude,
            longitude,
            radius: radiusKm,
            size: 8,
            tagging_mode,
            types,
            accessibilities,
            showVenueOptions,
          });
          setSuggestions(list);
          setIsSuggestOpen(true);
          // keep highlight at 0 (typed query)
        }, 200);
      }
    } else {
      // When input is cleared, show SearchSuggestionsDropdown if enabled
      clearSuggestions();
      if (showSearchSuggestions) {
        setIsSearchSuggestionsOpen(true);
      }
    }
  };

  const handleClear = () => {
    onValueChange("");
    clearSuggestions();
    if (onClear) onClear();
  };

  const handleEnter = () => {
    // If list is open, Enter will be handled in onKeyDown (using highlightIndex)
    if (isSuggestOpen) return;
    clearSuggestions();
    onSubmit(value);
  };

  const handleFocus = useCallback(async () => {
    if (suppressDropdown) return;

    const text = value.trim();

    // If input is empty and showSearchSuggestions is enabled, show SearchSuggestionsDropdown
    if (text.length === 0 && showSearchSuggestions) {
      setIsSearchSuggestionsOpen(true);
      setIsSuggestOpen(false);
      return;
    }

    if (!enableAutoSuggest) return;

    // Only show suggestions if there's text in the input
    if (text.length >= 2) {
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);

      // Fetch suggestions for the existing text
      const list = await getSuggestions(text, {
        fuzzy: true,
        highlights: true,
        latitude,
        longitude,
        radius: radiusKm,
        size: 8,
        tagging_mode,
        types,
        accessibilities,
        showVenueOptions,
      });

      setSuggestions(list);
    } else if (text.length === 1) {
      // Just show the query item without fetching suggestions
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);
      setSuggestions([]);
    }
  }, [
    value,
    latitude,
    longitude,
    radiusKm,
    tagging_mode,
    types,
    accessibilities,
    showVenueOptions,
    showSearchSuggestions,
    suppressDropdown,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle arrow keys for SearchSuggestionsDropdown (when input is empty)
    if (isSearchSuggestionsOpen && value.length === 0) {
      // SearchSuggestionsDropdown handles its own keyboard navigation via global listener
      return;
    }
    
    if (!enableAutoSuggest) return;
    if (!isSuggestOpen || displayItems.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) {
        setIsSuggestOpen(true);
        setHighlightIndex((prev) =>
          prev < 0 ? 0 : (prev + 1) % suggestions.length,
        );
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      setHighlightIndex((prev) =>
        prev < 0 ? 0 : (prev + 1) % displayItems.length,
      );
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setHighlightIndex((prev) =>
        prev <= 0 ? displayItems.length - 1 : prev - 1,
      );
      e.preventDefault();
    } else if (e.key === "Enter") {
      const indexToUse = highlightIndex >= 0 ? highlightIndex : 0;
      const item = displayItems[indexToUse];
      if (item) {
        if (suggestDebounceRef.current) {
          window.clearTimeout(suggestDebounceRef.current);
          suggestDebounceRef.current = null;
        }
        setIsSuggestOpen(false);
        setHighlightIndex(-1);
        setSuggestions([]);
        if (item.kind === "query") {
          onSubmit(sanitizeInput(value));
        } else {
          // Format the display text based on suggestion type
          const displayText = formatDisplayText(item.data, value);

          onSelectSuggestion({
            ...item.data,
            text: displayText,
            name: displayText,
          });
        }
      }
      e.preventDefault();
      e.stopPropagation();
    } else if (e.key === "Escape") {
      clearSuggestions();
      e.preventDefault();
    }
  };

  const handleClickItem = (
    item:
      | { kind: "query"; text: string }
      | { kind: "suggestion"; data: Suggestion },
  ) => {
    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
      suggestDebounceRef.current = null;
    }
    setIsSuggestOpen(false);
    setHighlightIndex(-1);
    setSuggestions([]);
    if (item.kind === "query") {
      onSubmit(sanitizeInput(value));
    } else {
      // Format the display text based on suggestion type
      const displayText = formatDisplayText(item.data, value);

      onSelectSuggestion({
        ...item.data,
        text: displayText,
        name: displayText,
      });
    }
  };

  // Handlers for SearchSuggestionsDropdown
  const handleNearbyPlacesClick = () => {
    // Track analytics
    AnalyticsService.trackGAEventHandler("searchNearbyPlacesClicked");
    AnalyticsService.trackMx("searchNearbyPlacesClicked", {});

    clearSuggestions();
    onNearbyPlacesClick?.();
  };

  const handleRecentSearchSelect = (search: RecentSearchResult) => {
    // Track analytics
    AnalyticsService.trackGAEventHandler("searchRecentSearchSelected");
    AnalyticsService.trackMx("searchRecentSearchSelected", {
      SearchText: search.search_term,
    });

    clearSuggestions();
    onRecentSearchSelect?.(search);
  };

  const handleNeighborhoodSelect = (neighborhood: NeighborhoodSuggestion) => {
    // Track analytics
    AnalyticsService.trackGAEventHandler("searchNeighborhoodSelected");
    AnalyticsService.trackMx("searchNeighborhoodSelected", {
      NeighborhoodName: neighborhood.name,
    });

    clearSuggestions();
    onNeighborhoodSelect?.(neighborhood);
  };

  const handleLoginPress = () => {
    clearSuggestions();
    onLoginPress?.();
  };

  const handleSearchSuggestionsClose = () => {
    setIsSearchSuggestionsOpen(false);
  };

  const handleContainerBlur = () => {
    // Use setTimeout to allow the browser to determine the new activeElement
    setTimeout(() => {
      // CheckIcon if focus moved outside the container
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        clearSuggestions();
      }
    }, 0);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)} onBlur={handleContainerBlur}>
      <FloatingLabelInput
        ref={inputRef}
        label={label}
        type="text"
        value={value}
        onChange={handleChange}
        onClear={handleClear}
        onEnter={handleEnter}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        borderless={true}
        role="combobox"
        aria-expanded={
          (isSuggestOpen && value.length > 0) || 
          (isSearchSuggestionsOpen && value.length === 0)
        }
        aria-controls={
          isSuggestOpen && value.length > 0 
            ? "suggestions-listbox" 
            : isSearchSuggestionsOpen && value.length === 0
            ? "search-suggestions-listbox"
            : undefined
        }
        aria-activedescendant={highlightIndex >= 0 ? `suggest-item-${highlightIndex}` : undefined}
        aria-autocomplete="list"
      />

      {/* SearchSuggestionsDropdown - shown when input is empty and focused */}
      {showSearchSuggestions &&
        isSearchSuggestionsOpen &&
        value.length === 0 && (
          <div className="absolute z-20 left-[-30px] md:left-0 right-0 top-full mt-2">
            <SearchSuggestionsDropdown
              isOpen={true}
              onNearbyPlacesClick={handleNearbyPlacesClick}
              onRecentSearchSelect={handleRecentSearchSelect}
              onNeighborhoodSelect={handleNeighborhoodSelect}
              onLoginPress={handleLoginPress}
              onClose={handleSearchSuggestionsClose}
              onKeyboardNavigate={() => {}}
              latitude={latitude}
              longitude={longitude}
            />
          </div>
     )}

      {/* Autosuggest dropdown - shown when user is typing */}
      {enableAutoSuggest && isSuggestOpen && value.length > 0 && (
        <div
          ref={suggestListRef}
          id="suggestions-listbox"
          role="listbox"
          className="absolute z-50 left-0 right-0 top-full mt-2 bg-bg-brand-secondary-default border border-border-token-neutral-minimal rounded shadow-lg max-h-80 overflow-y-auto"
        >
          {displayItems.map((item, idx) => (
            <button
              key={`item-${idx}`}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={idx === highlightIndex}
              className={cn(
                "w-full text-left px-4 py-2 focus:outline-none transition-colors",
                idx === highlightIndex
                  ? "bg-bg-brand-tertiary-active "
                  : "hover:bg-container-neutral-minimal",
              )}
              id={`suggest-item-${idx}`}
              onClick={() => handleClickItem(item)}
            >
              {item.kind === "query" ? (
                <div className="flex items-center gap-2 w-full">
                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0 min-w-[24px]">
                    <MagnifyingGlassIcon className="text-fg-brand-subtle" size={20} />
                  </div>
                  <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                    <span className="label-m-semibold text-fg-neutral-strong truncate max-w-full">
                      {value}
                    </span>
                    <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                      {i18n.get("pressEnterToSearch")}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 w-full">
                  <div className="flex items-center justify-center w-7 h-7 flex-shrink-0 min-w-[28px]">
                    {getSuggestionIcon(
                      item.data.suggestion_type,
                      item.data.business_type,
                      item.data.brand,
                      latitude,
                      longitude,
                    )}
                  </div>
                  <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                    <span className="label-m-semibold text-fg-neutral-strong truncate max-w-full">
                      {item.data.suggestion_type === "brand"
                        ? highlightName(
                            formatTextForDisplay(item.data.brand || ""),
                            value,
                          )
                        : item.data.suggestion_type === "location"
                          ? highlightName(
                              sanitizeInput(item.data.text || ""),
                              value,
                            )
                          : item.data.suggestion_type === "business_type"
                            ? highlightName(
                                formatTextForDisplay(
                                  item.data.business_type || "",
                                ),
                                value,
                              )
                            : highlightName(
                                item.data.name || item.data.text || "",
                                value,
                              )}
                    </span>
                    {item.data.suggestion_type === "brand" && (
                      <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                        {i18n.get("seeLocations")}
                      </span>
                    )}
                    {item.data.suggestion_type === "business_type" && (
                      <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                        {item.data.city_display || item.data.city || ""}
                      </span>
                    )}
                    {item.data.suggestion_type !== "brand" &&
                      item.data.suggestion_type !== "location" &&
                      item.data.suggestion_type !== "business_type" && (
                        <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                          {item.data.distanceText
                            ? `${item.data.distanceText} \u2022`
                            : ""}
                          {[item.data.address, item.data.city]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      )}
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
